import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Button } from '../ui/Button';
import { CheckCircle2Icon, CalendarIcon, ShieldCheckIcon } from 'lucide-react';
import { AnimatedScreen, successVariants, checkmarkVariants } from '../ui/AnimatedScreen';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
export interface ConsentSuccessScreenProps {
  bankName: string;
  selectedAccountsCount: number;
  expiryDate: string;
  onViewPlan: () => void;
  onViewDashboard: () => void;
}
export function ConsentSuccessScreen({
  bankName,
  selectedAccountsCount,
  expiryDate,
  onViewPlan,
  onViewDashboard
}: ConsentSuccessScreenProps) {
  useEffect(() => {
    // Trigger confetti celebration with OpenFinance-OS colors
    const duration = 2000;
    const end = Date.now() + duration;
    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: {
          x: 0,
          y: 0.6
        },
        colors: [OPENFINANCE_THEME.colors.primary500, OPENFINANCE_THEME.colors.accent500, OPENFINANCE_THEME.colors.primary400]
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: {
          x: 1,
          y: 0.6
        },
        colors: [OPENFINANCE_THEME.colors.primary500, OPENFINANCE_THEME.colors.accent500, OPENFINANCE_THEME.colors.primary400]
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    setTimeout(() => frame(), 300);
  }, []);
  return <AnimatedScreen data-id="element-640">
      <div className="space-y-6 text-center" data-id="element-641">
        {/* Animated Success Icon with teal/amber */}
        <motion.div variants={successVariants} initial="hidden" animate="show" className="flex justify-center" data-id="element-642">
          <div className="relative" data-id="element-643">
            <div className="w-24 h-24 rounded-full flex items-center justify-center shadow-xl" style={{
            background: OPENFINANCE_THEME.gradients.primary
          }} data-id="element-644">
              <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-645">
                <motion.path d="M5 13l4 4L19 7" variants={checkmarkVariants} initial="hidden" animate="show" data-id="element-646" />
              </svg>
            </div>
            {/* Pulsing ring effect with teal */}
            <motion.div animate={{
            scale: [1, 1.3, 1],
            opacity: [0.5, 0, 0.5]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute inset-0 rounded-full" style={{
            backgroundColor: OPENFINANCE_THEME.colors.primary400
          }} data-id="element-647" />
          </div>
        </motion.div>

        {/* Success Message with Fraunces */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4,
        duration: 0.3
      }} data-id="element-648">
          <h2 className="text-2xl font-bold mb-2" style={{
          fontFamily: OPENFINANCE_THEME.fonts.display,
          color: OPENFINANCE_THEME.colors.primary700
        }} data-id="element-649">
            Authorization Successful!
          </h2>
          <p className="text-sm leading-relaxed" style={{
          color: OPENFINANCE_THEME.colors.textLight,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-650">
            You've successfully connected your {bankName} account
          </p>
        </motion.div>

        {/* Success Details with teal accent */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.3
      }} className="p-5" style={{
        background: OPENFINANCE_THEME.gradients.card,
        border: `2px solid ${OPENFINANCE_THEME.colors.primary200}`,
        borderRadius: OPENFINANCE_THEME.radius.lg
      }} data-id="element-651">
          <div className="space-y-3" data-id="element-652">
            <div className="flex items-center justify-between text-sm" data-id="element-653">
              <div className="flex items-center gap-2" style={{
              color: OPENFINANCE_THEME.colors.textLight
            }} data-id="element-654">
                <CheckCircle2Icon className="w-4 h-4" style={{
                color: OPENFINANCE_THEME.colors.primary500
              }} data-id="element-655" />
                <span data-id="element-656">Accounts connected:</span>
              </div>
              <span className="font-bold" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-657">
                {selectedAccountsCount}{' '}
                {selectedAccountsCount === 1 ? 'account' : 'accounts'}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm" data-id="element-658">
              <div className="flex items-center gap-2" style={{
              color: OPENFINANCE_THEME.colors.textLight
            }} data-id="element-659">
                <CalendarIcon className="w-4 h-4" style={{
                color: OPENFINANCE_THEME.colors.primary500
              }} data-id="element-660" />
                <span data-id="element-661">Active until:</span>
              </div>
              <span className="font-bold" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-662">
                {expiryDate}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm" data-id="element-663">
              <div className="flex items-center gap-2" style={{
              color: OPENFINANCE_THEME.colors.textLight
            }} data-id="element-664">
                <ShieldCheckIcon className="w-4 h-4" style={{
                color: OPENFINANCE_THEME.colors.primary500
              }} data-id="element-665" />
                <span data-id="element-666">Status:</span>
              </div>
              <span className="font-bold" style={{
              color: OPENFINANCE_THEME.colors.primary600
            }} data-id="element-667">
                Active
              </span>
            </div>
          </div>
        </motion.div>

        {/* What Happens Next with amber accent */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6,
        duration: 0.3
      }} className="text-left p-4" style={{
        backgroundColor: OPENFINANCE_THEME.colors.accent300 + '20',
        border: `2px solid ${OPENFINANCE_THEME.colors.accent300}`,
        borderRadius: OPENFINANCE_THEME.radius.lg
      }} data-id="element-668">
          <div className="text-sm font-semibold mb-2" style={{
          color: OPENFINANCE_THEME.colors.primary700,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-669">
            What happens next:
          </div>
          <div className="space-y-2 text-xs" style={{
          color: OPENFINANCE_THEME.colors.textLight
        }} data-id="element-670">
            <div className="flex items-start gap-2" data-id="element-671">
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: OPENFINANCE_THEME.colors.accent500,
              color: 'white'
            }} data-id="element-672">
                <span className="text-xs font-bold" data-id="element-673">1</span>
              </div>
              <span data-id="element-674">VeloX can now access your authorized data securely</span>
            </div>
            <div className="flex items-start gap-2" data-id="element-675">
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: OPENFINANCE_THEME.colors.accent500,
              color: 'white'
            }} data-id="element-676">
                <span className="text-xs font-bold" data-id="element-677">2</span>
              </div>
              <span data-id="element-678">You'll receive notifications for any data access</span>
            </div>
            <div className="flex items-start gap-2" data-id="element-679">
              <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
              backgroundColor: OPENFINANCE_THEME.colors.accent500,
              color: 'white'
            }} data-id="element-680">
                <span className="text-xs font-bold" data-id="element-681">3</span>
              </div>
              <span data-id="element-682">Manage or revoke consent anytime in Settings</span>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7,
        duration: 0.3
      }} className="space-y-3 pt-2" data-id="element-683">
          <Button onClick={onViewDashboard} variant="accent" className="w-full" data-id="element-684">
            View Your Dashboard
          </Button>
          <button onClick={onViewPlan} className="w-full px-4 py-3 text-sm font-medium transition-colors" style={{
          color: OPENFINANCE_THEME.colors.primary500,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-685">
            Manage Consents
          </button>
        </motion.div>
      </div>
    </AnimatedScreen>;
}